/* eslint-disable */

import { useEffect, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../Components/ui/tabs'
import { AutoComplete } from '../../Components/ui/mf-search-input'

import DebtFunds from '../../Components/MutualFunds/DebtFunds'
import EquityFunds from '../../Components/MutualFunds/EquityFunds'
import HybridFunds from '../../Components/MutualFunds/HybridFunds'
import useMediaQuery from '../../hooks/useMediaQuery'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { get } from '../../http/restservice'
import useDebounce from '../../hooks/useDebounce'
import { messageModalState } from '../../store/Feedback'
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil'
import { AnimatePresence, motion } from 'framer-motion'
import { FabitsStarFunds, MFRecentlyViewed, MutualFundsHolding } from '../../store/MFStore'
import { toCurrency } from '../../utils/CurrencyUtil'
import MFNavbar from '../../Components/MutualFunds/MFNavbar'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../Components/ui/dialog'
import { Button } from '../../Components/ui/button'
import { Bug, Search } from 'lucide-react'
import { MobileSearchInput } from '../../Components/ui/mobile-search-input'
import env from '../../env/env'
import { isDematUser } from '../../utils/AuthUtil'
import { Tab } from '../../Components/ui/Tab'
import PostHogTracker from '../../utils/PostHogTracker'
import { DEBUG_FUND, isDebugSearchQuery } from '../../constants/debugFund'
import {
  AnimatedSearchItem,
  AnimatedSearchList,
  AnimatedSearchStatus,
} from '../../Components/ui/animated-search-results'

const AnimatedTabContent = ({ children }) => (
  <motion.div
    initial={{ x: 20, opacity: 0 }}
    animate={{ x: 0, opacity: 1 }}
    exit={{ x: -20, opacity: 0 }}
    transition={{ duration: 0.1, ease: 'easeInOut' }}
  >
    {children}
  </motion.div>
)

const MutualFunds = () => {
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const [tabValue, setTabValue] = useState(searchParams.get('assetClass') || 'Equity')
  const [isLoading, setIsLoading] = useState(false)

  const [searchLoading, setSearchLoading] = useState(false)
  const [searchData, setSearchData] = useState([])
  const [query, setQuery] = useState('')
  const debouncedSearchTerm = useDebounce(query, 500)

  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const location = useLocation()

  const mfHolding = isDematUser() && useRecoilValueLoadable(MutualFundsHolding)

  // const mfBasketHolding = isDematUser() && useRecoilValueLoadable(MFBasketHoldings)

  const paramsObject = Object.fromEntries(searchParams.entries())
  const [mutualFundsData, setMutualFundsData] = useState([])

  const [totalPages, setTotalPages] = useState(0)

  const setMessageModalState = useSetRecoilState(messageModalState)

  const [isOpen, setIsOpen] = useState(false)
  const fabStarFunds = useRecoilValueLoadable(FabitsStarFunds)
  const recentlyViewed = useRecoilValueLoadable(MFRecentlyViewed)

  const handleSearchOpen = () => {
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
    // Track MF page viewed
    PostHogTracker.mfPageViewed(tabValue)
  }, [])

  useEffect(() => {
    const fetchMFData = async () => {
      setIsLoading(true)

      if (paramsObject?.search) {
        delete paramsObject.assetClass
      }

      if (paramsObject.page && paramsObject.page != 0) {
        paramsObject.page = paramsObject.page - 1
      } else {
        paramsObject.page = 0
      }

      const response = await get(
        'https://apimywealth.fabits.com/mutualfundservice/api/mfData/allFunds',
        {},
        paramsObject,
      )

      setIsLoading(false)
      if (response.isError) {
        setMutualFundsData([])
        setTotalPages(0)
        setMessageModalState({
          message: response?.response?.message,
          severity: 'error',
        })
      }

      if (!response.isError) {
        setMutualFundsData(response?.funds)
        setTotalPages(response?.totalPages)
      }
    }

    if (location.pathname === '/dashboard/mutual-funds') fetchMFData()
  }, [JSON.stringify(paramsObject)])

  useEffect(() => {
    const searchFunds = async () => {
      if (isDebugSearchQuery(debouncedSearchTerm)) {
        setSearchLoading(false)
        setSearchData([DEBUG_FUND])
        return
      }

      setSearchLoading(true)
      // Track fund search initiated
      PostHogTracker.fundSearchInitiated(debouncedSearchTerm)

      const response = await get(
        `/mutualfundservice/api/mfData/allFunds?search=${debouncedSearchTerm}`,
      )
      setSearchLoading(false)

      if (response.isError) {
        setSearchData([])
      }

      if (!response.isError) {
        setSearchData(response?.funds)
      }
    }

    if (debouncedSearchTerm) {
      searchFunds()
    } else {
      setSearchData([])
    }
  }, [debouncedSearchTerm])

  const clearAllFilter = (assetClass) => {
    // Track fund category selected
    PostHogTracker.fundCategorySelected(assetClass)

    setSearchParams({
      assetClass: assetClass,
      page: 0,
      size: 10,
      sortBy: 'threeYearRet',
      sortDir: 'desc',
    })
  }

  const hideContentPaths = [
    '/dashboard/mutual-funds/investments',
    '/dashboard/mutual-funds/orders',
    '/dashboard/mutual-funds/sips',
  ] // Update based on your child routes

  const shouldHideParentContent = hideContentPaths.includes(location.pathname)

  const handleSearchCancel = () => {
    setIsOpen(false)
    setQuery('')
  }

  const handleFabStarNavigate = (fund) => {
    setIsOpen(false)

    if (fund?.isDebugFund) {
      navigate('/dashboard/debug-popups')
      return
    }

    // Track fund card clicked
    PostHogTracker.fundCardClicked(fund.schemeName, fund.schemeCode)

    navigate(`/dashboard/mutual-funds/details`, {
      state: {
        fundDetails: fund,
        schemeCode: fund.schemeCode,
      },
    })
  }

  const getTotalAmount = () => {
    // const basketTotal =
    //   mfBasketHolding?.contents?.holdings?.reduce(
    //     (acc, order) => acc + Number(order.currentNav * order.netUnits),
    //     0,
    //   ) || 0

    const mfIndividualTotal =
      mfHolding?.contents?.mainData?.filter((item) => item?.isOutsideData === 0)?.reduce((sum, item) => sum + item.holding, 0) || 0

    return mfIndividualTotal
  }

  const getTotalInvestedAmount = () => {
    const totalIndividual =
      mfHolding?.contents?.mainData?.filter((item) => item?.isOutsideData === 0)?.reduce((sum, item) => sum + Number(item.netInvestedAmt), 0) ||
      0

    // const totalBasket =
    //   mfBasketHolding?.contents?.holdings?.reduce((acc, order) => {
    //     return acc + Number(order.netInvestedAmount)
    //   }, 0) || 0

    return totalIndividual
  }

  const getReturns = (mfHolding) => {
    const total =
      mfHolding?.contents?.mainData?.filter((item) => item?.isOutsideData === 0)?.reduce((sum, item) => sum + item.holding, 0) + getTotalAmount()
    const invested =
      mfHolding?.contents?.mainData?.filter((item) => item?.isOutsideData === 0)?.reduce((sum, item) => sum + item.netInvestedAmt, 0) +
      getTotalInvestedAmount()

    // Calculate returns in percentage
    const returns = ((total - invested) / invested) * 100

    return returns < 0 ? toCurrency(returns) : toCurrency(returns)
  }

  const tabs = ['Debt', 'Equity', 'Hybrid']

  return (
    <>
      {isDesktop ? (
        <>
          <div className='fabits-mf-hero py-2'>
            {/* <MFNavbar /> */}

            <div className={`max-w-[1128px] mx-auto font-DMSan py-12`}>
              <div className='flex justify-center items-center py-3 max-h-12'>
                {mfHolding?.state === 'hasValue' &&
                  mfHolding?.contents?.mainData?.length > 0 &&
                  location.pathname != '/dashboard/mutual-funds/sips' &&
                  location.pathname != '/dashboard/mutual-funds/investments' &&
                  location.pathname != '/dashboard/mutual-funds/orders' && (
                    <div>
                      <AutoComplete
                        options={searchData}
                        emptyMessage='No funds available'
                        placeholder='Search for funds'
                        searchLoading={searchLoading}
                        onValueChange={setQuery}
                        value={query}
                        fabStarFunds={fabStarFunds}
                        recentlyViewedFunds={recentlyViewed}
                      />
                    </div>
                  )}
              </div>

              {location.pathname != '/dashboard/mutual-funds/orders' &&
                location.pathname != '/dashboard/mutual-funds/sips' &&
                (mfHolding?.state === 'hasValue' && mfHolding?.contents?.mainData?.length > 0 ? (
                  <>
                    <div className='mt-12 flex items-end justify-center gap-x-12 font-IBM'>
                      <div>
                        <p className='text-center font-IBM font-semibold text-white'>
                          ₹ <>{toCurrency(getTotalInvestedAmount())}</>
                        </p>
                        <p className='text-center font-IBM text-sm font-medium text-primary-100'>
                          Total Invested Amount
                        </p>
                      </div>
                      <div>
                        <h3 className='text-center font-IBM text-[32px] font-semibold leading-[42px] tracking-normal text-white'>
                          ₹{' '}
                          {(() => {
                            const totalHolding = getTotalAmount().toFixed(2)
                            const [integerPart, decimalPart] = totalHolding.split('.')

                            return (
                              <>
                                {toCurrency(integerPart)}
                                <span className='text-[20px] align-top'>.{decimalPart}</span>
                              </>
                            )
                          })()}
                        </h3>
                        <p className='text-center font-IBM text-sm font-medium text-primary-100'>
                          Total Current MF Portfolio
                        </p>
                      </div>
                      <div>
                        <p className={`text-center font-IBM font-semibold text-white`}>
                          {getReturns(mfHolding)}%
                        </p>
                        <p className='text-center font-IBM text-sm font-medium text-primary-100'>
                          Overall Returns
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className='font-semibold text-[32px] text-white text-center my-6'>
                      Mutual Funds
                    </h1>
                    <div className='max-w-[552px] mx-auto'>
                      <AutoComplete
                        options={searchData}
                        emptyMessage='No funds available'
                        placeholder='Search for funds'
                        searchLoading={searchLoading}
                        onValueChange={setQuery}
                        value={query}
                        fabStarFunds={fabStarFunds}
                        recentlyViewedFunds={recentlyViewed}
                      />
                    </div>
                  </>
                ))}
            </div>
          </div>

          {!shouldHideParentContent && (
            <div className='fabits-dark-section bg-white'>
              <div className='max-w-[1128px] mx-auto pt-6 pb-8'>
                <div>
                  <div className='mx-auto flex h-12 w-full max-w-[520px] items-center justify-center overflow-hidden rounded-[999px] border border-black-200 bg-white p-1 shadow-[0_12px_32px_rgba(4,3,19,0.08)]'>
                    {tabs.map((tab, index) => (
                      <button
                        type='button'
                        key={tab + index}
                        className={`flex h-10 min-w-0 flex-1 basis-0 items-center justify-center rounded-[999px] px-4 font-IBM text-sm font-semibold leading-5 transition-all duration-200 ${
                          tabValue === tab
                            ? 'bg-primary-600 text-white shadow-[0_10px_24px_rgba(40,24,255,0.18)]'
                            : 'text-black-500 hover:text-black-700'
                        }`}
                        onClick={() => {
                          setTabValue(tab)
                          clearAllFilter(tab)
                        }}
                      >
                        <span className='truncate'>{tab}</span>
                      </button>
                    ))}
                  </div>

                  <AnimatePresence mode='wait'>
                    {tabs.map(
                      (tab, index) =>
                        tabValue === tab && (
                          <motion.div
                            key={tab + index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {tab === 'Debt' && (
                              <DebtFunds
                                searchValue={''}
                                mutualFundsData={mutualFundsData}
                                totalPages={totalPages}
                                isLoading={isLoading}
                              />
                            )}
                            {tab === 'Equity' && (
                              <EquityFunds
                                searchValue={''}
                                mutualFundsData={mutualFundsData}
                                totalPages={totalPages}
                                isLoading={isLoading}
                              />
                            )}
                            {tab === 'Hybrid' && (
                              <HybridFunds
                                searchValue={''}
                                mutualFundsData={mutualFundsData}
                                totalPages={totalPages}
                                isLoading={isLoading}
                              />
                            )}
                          </motion.div>
                        ),
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className='bg-white dark:bg-[var(--fb-bg)]'>
            <MFNavbar handleSearchOpen={handleSearchOpen} />

            <Dialog
              open={isOpen}
              onOpenChange={handleSearchOpen}
              aria-labelledby='dialog-title'
              aria-describedby='dialog-description'
            >
              <DialogContent className='font-DMSans h-full w-screen md:h-[50vh] duration-300 ease-out flex flex-col justify-start border rounded-none overflow-y-scroll hide-scrollbar'>
                <DialogHeader>
                  <DialogTitle>
                    <div className='flex items-center mb-4'>
                      <MobileSearchInput
                        placeholder='Search for funds'
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        handleSearchCancel={handleSearchCancel}
                      />
                    </div>
                  </DialogTitle>
                </DialogHeader>

                {!debouncedSearchTerm && isDematUser() && (
                  <div className='flex flex-col'>
                    <div className='flex gap-x-2 items-center'>
                      <Button
                        variant='simple'
                        size='simple'
                        onClick={() => {
                          handleSearchCancel(), navigate('/dashboard/mutual-funds/orders')
                        }}
                        className={`rounded-full font-medium text-xs border border-primary-200 px-4 py-2 bg-background text-primary-500`}
                      >
                        Orders
                      </Button>
                      <Button
                        variant='simple'
                        size='simple'
                        onClick={() => {
                          handleSearchCancel(), navigate('/dashboard/mutual-funds/sips')
                        }}
                        className={`rounded-full font-medium text-xs border border-primary-200 px-4 py-2 bg-background text-primary-500`}
                      >
                        SIPs
                      </Button>
                      <Button
                        variant='simple'
                        size='simple'
                        onClick={() => {
                          handleSearchCancel(), navigate('/dashboard/mutual-funds/investments')
                        }}
                        className={`rounded-full font-medium text-xs border border-primary-200 px-4 py-2 bg-background text-primary-500`}
                      >
                        Investments
                      </Button>
                    </div>
                  </div>
                )}

                {!debouncedSearchTerm && recentlyViewed?.state != 'hasValue' && (
                  <div className='mt-5'>
                    <h2 className='font-semibold text-primary-700 leading-6 tracking-[-4%]'>
                      Fabstar funds
                    </h2>
                    <div>
                      {fabStarFunds.state === 'hasValue' &&
                        fabStarFunds?.contents?.data?.map((fund) => (
                          <div
                            className='cursor-pointer flex gap-x-2 items-center py-4 border-b-[1px] border-primary-50'
                            key={fund?.amcCode}
                            onClick={() => handleFabStarNavigate(fund)}
                          >
                            <img
                              src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
                              className='h-9 w-9 rounded-sm'
                            />

                            <div className='w-full'>
                              <div className='flex justify-between'>
                                <h2 className='font-semibold leading-5 text-sm text-black-500 text-nowrap'>
                                  {fund?.legalName
                                    ? fund?.legalName?.slice(0, 25)
                                    : fund?.sName?.slice(0, 25)}
                                  ...
                                </h2>

                                <p
                                  className={`font-semibold leading-5 text-sm text-secondary-600 text-nowrap`}
                                >
                                  {fund?.threeYrRet.toFixed(2)}%{' '}
                                  <span className='text-xs'>(3Y)</span>
                                </p>
                              </div>
                              <p className='text-xs text-black-400 font-medium leading-4'>
                                Starts at{' '}
                                <span className='text-xs text-black-500 font-semibold'>
                                  ₹ {toCurrency(fund?.minInvt)}
                                </span>
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {!debouncedSearchTerm && (
                  <div className='mt-5'>
                    <h2 className='font-semibold text-primary-700 leading-6 tracking-[-4%]'>
                      Recently viewed funds
                    </h2>
                    <div>
                      {fabStarFunds.state === 'hasValue' &&
                        fabStarFunds?.contents?.data?.map((fund) => (
                          <div
                            className='cursor-pointer flex gap-x-2 items-center py-4 border-b-[1px] border-primary-50'
                            key={fund?.amcCode}
                            onClick={() => handleFabStarNavigate(fund)}
                          >
                            <img
                              src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
                              className='h-9 w-9 rounded-sm'
                            />

                            <div className='w-full'>
                              <div className='flex justify-between'>
                                <h2 className='font-semibold leading-5 text-sm text-black-500 text-nowrap'>
                                  {fund?.legalName
                                    ? fund?.legalName?.slice(0, 25)
                                    : fund?.sName?.slice(0, 25)}
                                  ...
                                </h2>

                                <p
                                  className={`font-semibold leading-5 text-sm text-secondary-600 text-nowrap`}
                                >
                                  {fund?.threeYrRet.toFixed(2)}%{' '}
                                  <span className='text-xs'>(3Y)</span>
                                </p>
                              </div>
                              <p className='text-xs text-black-400 font-medium leading-4'>
                                Starts at{' '}
                                <span className='text-xs text-black-500 font-semibold'>
                                  ₹ {toCurrency(fund?.minInvt)}
                                </span>
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                <AnimatePresence mode='wait'>
                  {searchLoading ? (
                    <AnimatedSearchStatus key='mobile-search-loading'>
                      Loading...
                    </AnimatedSearchStatus>
                  ) : debouncedSearchTerm && searchData.length === 0 ? (
                    <AnimatedSearchStatus key='mobile-search-empty'>
                      No funds available
                    </AnimatedSearchStatus>
                  ) : debouncedSearchTerm ? (
                    <AnimatedSearchList
                      key='mobile-search-results'
                      motionKey={`mobile-search-${debouncedSearchTerm}`}
                    >
                      {searchData.map((fund) => (
                        <AnimatedSearchItem
                          className='flex min-w-0 gap-x-2 items-center py-4 border-b-[1px] border-primary-50'
                          key={fund?.schemeCode || fund?.amcCode}
                          onClick={() => handleFabStarNavigate(fund)}
                        >
                          {fund?.isDebugFund ? (
                            <div className='h-9 w-9 shrink-0 rounded-sm bg-primary-50 text-primary-500 flex items-center justify-center'>
                              <Bug className='h-5 w-5' />
                            </div>
                          ) : (
                            <img
                              src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
                              className='h-9 w-9 shrink-0 rounded-sm'
                            />
                          )}

                          <div className='min-w-0 flex-1'>
                            <div className='flex min-w-0 items-start justify-between gap-3'>
                              <h2 className='min-w-0 flex-1 truncate font-semibold leading-5 text-sm text-black-500'>
                                {fund?.isDebugFund
                                  ? fund?.legalName
                                  : `${
                                      fund?.legalName
                                        ? fund?.legalName?.slice(0, 25)
                                        : fund?.sName?.slice(0, 25)
                                    }...`}
                              </h2>

                              <p
                                className={`shrink-0 font-semibold leading-5 text-sm text-secondary-600 text-nowrap`}
                              >
                                {fund?.isDebugFund ? (
                                  <span className='text-xs'>Test panel</span>
                                ) : (
                                  <>
                                    {fund?.threeYrRet.toFixed(2)}%{' '}
                                    <span className='text-xs'>(3Y)</span>
                                  </>
                                )}
                              </p>
                            </div>
                            <p className='text-xs text-black-400 font-medium leading-4'>
                              {fund?.isDebugFund ? (
                                'Trigger every app popup from one page'
                              ) : (
                                <>
                                  Starts at{' '}
                                  <span className='text-xs text-black-500 font-semibold'>
                                    ₹ {toCurrency(fund?.minInvt)}
                                  </span>
                                </>
                              )}
                            </p>
                          </div>
                        </AnimatedSearchItem>
                      ))}
                    </AnimatedSearchList>
                  ) : null}
                </AnimatePresence>
              </DialogContent>
            </Dialog>

            <div className='fabits-mf-hero h-auto p-5 mx-auto w-full'>
              {location.pathname != '/dashboard/mutual-funds/orders' &&
                (mfHolding?.state === 'hasValue' && mfHolding?.contents?.mainData?.length > 0 ? (
                  <>
                    <div className='mt-12 flex flex-col items-center justify-center gap-y-6 font-IBM'>
                      <div>
                        <h3 className='text-center font-IBM text-[32px] font-semibold leading-[42px] tracking-normal text-white'>
                          ₹{' '}
                          {(() => {
                            const totalHolding = (
                              mfHolding?.contents?.mainData?.reduce(
                                (sum, item) => sum + item.holding,
                                0,
                              ) || 0
                            ).toFixed(2)
                            const [integerPart, decimalPart] = totalHolding.split('.')

                            return (
                              <>
                                {toCurrency(integerPart)}
                                <span className='text-[20px] align-top'>.{decimalPart}</span>
                              </>
                            )
                          })()}
                        </h3>
                        <p className='text-center font-IBM text-sm font-medium text-primary-100'>
                          Total Current MF Portfolio
                        </p>
                      </div>

                      <div className='flex w-full items-center justify-between gap-4'>
                        <div className='min-w-0'>
                          <p className='text-start font-IBM font-semibold text-white'>
                            ₹{' '}
                            {toCurrency(
                              mfHolding?.contents?.mainData
                                ?.reduce((sum, item) => sum + item.netInvestedAmt, 0)
                                ?.toFixed(2),
                            )}
                          </p>
                          <p className='text-start font-IBM text-sm font-medium text-primary-100'>
                            Total Invested Amount
                          </p>
                        </div>
                        <div className='min-w-0'>
                          <p className={`text-right font-IBM font-semibold text-white`}>
                            {getReturns(mfHolding)}%
                          </p>
                          <p className='text-right font-IBM text-sm font-medium text-primary-100'>
                            Returns
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className='font-semibold text-[32px] text-white text-center my-6'>
                      Mutual Funds
                    </h1>
                    <div className='max-w-[552px] mx-auto'>
                      <div
                        className='fabits-search-shell relative flex items-center rounded-full'
                        onClick={() => setIsOpen(true)}
                      >
                        <Search className='absolute left-4 h-[14px] w-[14px]' />

                        <input
                          placeholder='Search for funds'
                          className='pl-12 pr-24 flex h-10 w-full rounded-full border-0 bg-transparent text-sm font-normal ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[var(--fb-text-subtle)] placeholder:font-normal placeholder:text-sm placeholder:leading-5 focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50'
                        />
                      </div>
                    </div>
                  </>
                ))}
            </div>
          </div>

          {!shouldHideParentContent && (
            <div className='fabits-dark-section bg-white pb-28'>
              <div className='max-w-[1128px] mx-auto pt-4 pb-8'>
                <div>
                  <Tabs defaultValue='Debt' value={tabValue} onValueChange={setTabValue}>
                    <TabsList className='mx-auto grid h-12 w-full max-w-[520px] grid-cols-3 overflow-hidden rounded-[999px] border border-black-200 bg-white p-1 shadow-[0_12px_32px_rgba(4,3,19,0.08)]'>
                      <TabsTrigger
                        className='h-10 min-w-0 px-2 font-IBM text-sm font-semibold leading-5 text-black-500 data-[state=active]:bg-primary-600 data-[state=active]:text-white'
                        value='Debt'
                        onClick={() => clearAllFilter('Debt')}
                      >
                        <span className='truncate'>Debt</span>
                      </TabsTrigger>
                      <TabsTrigger
                        className='h-10 min-w-0 px-2 font-IBM text-sm font-semibold leading-5 text-black-500 data-[state=active]:bg-primary-600 data-[state=active]:text-white'
                        value='Equity'
                        onClick={() => clearAllFilter('Equity')}
                      >
                        <span className='truncate'>Equity</span>
                      </TabsTrigger>
                      <TabsTrigger
                        className='h-10 min-w-0 px-2 font-IBM text-sm font-semibold leading-5 text-black-500 data-[state=active]:bg-primary-600 data-[state=active]:text-white'
                        value='Hybrid'
                        onClick={() => clearAllFilter('Hybrid')}
                      >
                        <span className='truncate'>Hybrid</span>
                      </TabsTrigger>
                    </TabsList>

                    <AnimatePresence mode='wait'>
                      <TabsContent value='Debt'>
                        <AnimatedTabContent>
                          <DebtFunds
                            searchValue={''}
                            mutualFundsData={mutualFundsData}
                            totalPages={totalPages}
                            isLoading={isLoading}
                          />
                        </AnimatedTabContent>
                      </TabsContent>
                    </AnimatePresence>

                    <AnimatePresence mode='wait'>
                      <TabsContent value='Equity'>
                        <AnimatedTabContent>
                          <EquityFunds
                            searchValue={''}
                            mutualFundsData={mutualFundsData}
                            totalPages={totalPages}
                            isLoading={isLoading}
                          />
                        </AnimatedTabContent>
                      </TabsContent>
                    </AnimatePresence>

                    <AnimatePresence mode='wait'>
                      <TabsContent value='Hybrid'>
                        <AnimatedTabContent>
                          <HybridFunds
                            searchValue={''}
                            mutualFundsData={mutualFundsData}
                            totalPages={totalPages}
                            isLoading={isLoading}
                          />
                        </AnimatedTabContent>
                      </TabsContent>
                    </AnimatePresence>
                  </Tabs>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default MutualFunds
